'use client';

import { Button } from '@heidi/design-system/button';
import {
  Spotlight,
  SpotlightArrow,
  SpotlightBody,
  SpotlightClose,
  SpotlightContent,
  SpotlightDescription,
  SpotlightFooter,
  SpotlightHeader,
  SpotlightImage,
  SpotlightProgress,
  SpotlightTitle,
} from '@heidi/design-system/spotlight';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import type { ReactElement } from 'react';

import type { CoachMarkProps } from './coach-mark.types';
import { useAnchorElement } from './hooks/use-anchor-element';

/**
 * SpotlightContent props that keep the coach-mark persistent while letting
 * clicks on the anchor itself pass through (so anchoring to an interactive
 * element doesn't swallow its click).
 */
const makeContentProps = (anchorEl: HTMLElement | null) => ({
  onOpenAutoFocus: (e: Event) => e.preventDefault(),
  onCloseAutoFocus: (e: Event) => e.preventDefault(),
  onInteractOutside: (e: Event) => e.preventDefault(),
  onPointerDownOutside: (e: Event) => {
    if (anchorEl) {
      const pointerEvent = (e as unknown as { detail: { originalEvent: PointerEvent } })
        .detail?.originalEvent;
      if (pointerEvent && anchorEl.contains(pointerEvent.target as Node)) {
        return; // let the click reach the anchor
      }
    }
    e.preventDefault();
  },
  onFocusOutside: (e: Event) => e.preventDefault(),
});

/**
 * Reusable feature-discovery coach-mark: an anchored {@link Spotlight} with an
 * optional illustration, title, description, optional step counter, and a
 * single primary action. Visibility/seen-state and step orchestration belong to
 * the caller — this renders one step and nothing until its anchor exists.
 *
 * Modelled on the demo-flow coach-marks and `HeidiVerifySpotlight`; created as a
 * shared component so new surfaces don't hand-roll the Spotlight composition.
 */
export function CoachMark({
  anchorSelector,
  side = 'bottom',
  align = 'center',
  sideOffset = 8,
  icon,
  title,
  description,
  image,
  progress,
  primaryLabel,
  onPrimary,
  onDismiss,
  'data-testid': dataTestid,
}: CoachMarkProps): ReactElement | null {
  const anchorEl = useAnchorElement(anchorSelector);

  if (!anchorEl) return null;

  return (
    <Spotlight open>
      <PopoverPrimitive.Anchor virtualRef={{ current: anchorEl }} />
      <SpotlightContent
        side={side}
        align={align}
        sideOffset={sideOffset}
        onEscapeKeyDown={onDismiss}
        {...makeContentProps(anchorEl)}
        data-testid={dataTestid}
      >
        <SpotlightArrow width={12} height={8} />
        <SpotlightHeader icon={icon}>
          <SpotlightTitle>{title}</SpotlightTitle>
          <SpotlightClose onClick={onDismiss} />
        </SpotlightHeader>
        {image ? (
          <SpotlightBody className="bg-surface">
            <SpotlightImage>{image}</SpotlightImage>
          </SpotlightBody>
        ) : null}
        <SpotlightDescription>{description}</SpotlightDescription>
        <SpotlightFooter>
          {progress != null ? <SpotlightProgress>{progress}</SpotlightProgress> : null}
          <Button
            variant="secondary"
            size="sm"
            onClick={onPrimary}
            className="bg-surface text-text-primary hover:bg-background-secondary"
          >
            {primaryLabel}
          </Button>
        </SpotlightFooter>
      </SpotlightContent>
    </Spotlight>
  );
}
